import { useMemo, useState } from "react"
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@nasti/ui/carousel"
import { Dialog, DialogContent, DialogTitle } from "@nasti/ui/dialog"
import { Skeleton } from "@nasti/ui/skeleton"
import { Button } from "@nasti/ui/button"
import { Pencil } from "lucide-react"

import { BatchCleaningModal } from "@/components/inventory/modals/BatchCleaningModal"
import {
  type BatchCleaningPhotoSignedUrl,
  type CleaningPhotoStage,
  useBatchCleaningPhotos,
} from "@/hooks/useBatchCleaningPhotos"
import {
  type BatchWithCurrentLocationAndSpecies,
  useBatchHistory,
} from "@/hooks/useBatches"
import { useBatchCleaning } from "@/hooks/useCleanBatch"

const stageLabels: Record<CleaningPhotoStage, string> = {
  before: "Before cleaning",
  after: "After cleaning",
}

const getCleaningId = (
  event:
    | {
        creation_event: string | null
        event_details: unknown
      }
    | null
    | undefined,
) => {
  if (
    event?.creation_event !== "cleaning" ||
    !event.event_details ||
    typeof event.event_details !== "object" ||
    Array.isArray(event.event_details)
  ) {
    return undefined
  }

  const cleaningId = (event.event_details as Record<string, unknown>)
    .batch_cleaning_id

  return typeof cleaningId === "string" ? cleaningId : undefined
}

const PhotoThumbnail = ({
  photo,
  index,
  onOpen,
}: {
  photo: BatchCleaningPhotoSignedUrl
  index: number
  onOpen: (index: number) => void
}) => (
  <button
    type="button"
    className="focus-visible:ring-ring group h-16 w-16 cursor-zoom-in overflow-hidden rounded-md border bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
    aria-label={`Open ${stageLabels[photo.stage].toLowerCase()} photo ${index + 1}`}
    onClick={() => onOpen(index)}
  >
    <img
      src={photo.signedUrl}
      alt=""
      className="h-full w-full object-cover transition-transform group-hover:scale-105"
    />
  </button>
)

const CleaningPhotoViewer = ({
  photos,
  selectedIndex,
  onClose,
}: {
  photos: BatchCleaningPhotoSignedUrl[]
  selectedIndex: number
  onClose: () => void
}) => (
  <Dialog open onOpenChange={(isOpen) => !isOpen && onClose()}>
    <DialogContent className="w-[calc(100vw-2rem)] max-w-5xl">
      <DialogTitle>Cleaning photos</DialogTitle>
      <Carousel
        opts={{ startIndex: selectedIndex }}
        className="mx-8"
        aria-label="Cleaning photo gallery"
      >
        <CarouselContent>
          {photos.map((photo, index) => (
            <CarouselItem key={photo.id}>
              <figure className="flex flex-col items-center gap-3">
                <img
                  src={photo.signedUrl}
                  alt={`${stageLabels[photo.stage]} photo ${index + 1} of ${photos.length}`}
                  className="max-h-[75vh] w-full rounded-md object-contain"
                />
                <figcaption className="text-muted-foreground text-center text-sm">
                  <span className="text-foreground font-medium">
                    {stageLabels[photo.stage]}
                  </span>
                  {photo.caption && <> · {photo.caption}</>}
                  {photo.uploaded_at && (
                    <> · {new Date(photo.uploaded_at).toLocaleString()}</>
                  )}
                  <span className="ml-2">
                    {index + 1} of {photos.length}
                  </span>
                </figcaption>
              </figure>
            </CarouselItem>
          ))}
        </CarouselContent>
        {photos.length > 1 && (
          <>
            <CarouselPrevious />
            <CarouselNext />
          </>
        )}
      </Carousel>
    </DialogContent>
  </Dialog>
)

export const BatchCleaningPhotos = ({
  batch,
}: {
  batch: BatchWithCurrentLocationAndSpecies
}) => {
  const [selectedIndex, setSelectedIndex] = useState<number>()
  const [isEditing, setIsEditing] = useState(false)
  const { data: history, isLoading: isHistoryLoading } = useBatchHistory(
    batch.id,
  )
  const cleaningId = getCleaningId(history)
  const { data: cleaning, isLoading: isCleaningLoading } =
    useBatchCleaning(cleaningId)
  const {
    data: photos = [],
    isLoading: arePhotosLoading,
    isError: arePhotosError,
  } = useBatchCleaningPhotos(cleaningId)

  const photosByStage = useMemo(
    () => ({
      before: photos.filter((photo) => photo.stage === "before"),
      after: photos.filter((photo) => photo.stage === "after"),
    }),
    [photos],
  )

  if (isHistoryLoading) return null
  if (!cleaningId) return null

  return (
    <>
      <div className="col-span-full space-y-2">
        <div className="flex items-center gap-1">
          <span className="font-medium">Cleaning record</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            title="Edit cleaning record"
            aria-label="Edit cleaning record"
            disabled={isCleaningLoading || !cleaning}
            onClick={() => setIsEditing(true)}
          >
            <Pencil className="h-4 w-4" />
          </Button>
        </div>

        {cleaning?.cleaning_notes && (
          <div className="text-muted-foreground whitespace-pre-wrap text-xs">
            {cleaning.cleaning_notes}
          </div>
        )}

        {arePhotosLoading && (
          <div className="flex gap-2">
            <Skeleton className="h-16 w-16" />
            <Skeleton className="h-16 w-16" />
          </div>
        )}

        {arePhotosError && (
          <div className="text-destructive text-xs">
            Cleaning photos could not be loaded.
          </div>
        )}

        {!arePhotosLoading && !arePhotosError && photos.length > 0 && (
          <div className="flex flex-wrap gap-4">
            {(["before", "after"] as const).map((stage) => {
              const stagePhotos = photosByStage[stage]
              if (stagePhotos.length === 0) return null

              return (
                <div key={stage} className="space-y-1">
                  <div className="text-muted-foreground text-xs">
                    {stageLabels[stage]}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {stagePhotos.map((photo) => (
                      <PhotoThumbnail
                        key={photo.id}
                        photo={photo}
                        index={photos.indexOf(photo)}
                        onOpen={setSelectedIndex}
                      />
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {isEditing && cleaning && (
        <BatchCleaningModal
          isOpen={isEditing}
          onClose={() => setIsEditing(false)}
          batch={batch}
          instance={cleaning}
        />
      )}

      {selectedIndex !== undefined && (
        <CleaningPhotoViewer
          photos={photos}
          selectedIndex={selectedIndex}
          onClose={() => setSelectedIndex(undefined)}
        />
      )}
    </>
  )
}
